class WidthRangeFilter extends HTMLElement {
  constructor() {
    super();

    this.rangeMin = this.querySelector('[data-width-range-min]');
    this.rangeMax = this.querySelector('[data-width-range-max]');
    this.numberMin = this.querySelector('[data-width-number-min]');
    this.numberMax = this.querySelector('[data-width-number-max]');
    this.parameters = this.querySelector('[data-width-filter-parameters]');
    this.parameterName = this.parameters ? this.parameters.dataset.paramName : '';
    this.track = this.querySelector('.width-filter__range');
    this.applyButton = this.querySelector('[data-width-apply]')
      || this.closest('.mobile-facets__submenu')?.querySelector('[data-width-apply]');

    if (!this.rangeMin || !this.rangeMax || !this.numberMin || !this.numberMax || !this.parameters || !this.parameterName || !this.track || !this.applyButton) {
      return;
    }

    this.minimum = Number(this.dataset.min);
    this.maximum = Number(this.dataset.max);
    this.values = this.readValues();

    if (!Number.isFinite(this.minimum) || !Number.isFinite(this.maximum) || this.values.length === 0) {
      return;
    }

    this.rangeMin.addEventListener('input', (event) => {
      event.stopPropagation();
      this.updateFromRange('min');
    });
    this.rangeMax.addEventListener('input', (event) => {
      event.stopPropagation();
      this.updateFromRange('max');
    });
    this.numberMin.addEventListener('input', (event) => {
      event.stopPropagation();
      this.updateNumberDraft('min');
    });
    this.numberMax.addEventListener('input', (event) => {
      event.stopPropagation();
      this.updateNumberDraft('max');
    });
    this.numberMin.addEventListener('change', () => this.commitNumberInput('min'));
    this.numberMax.addEventListener('change', () => this.commitNumberInput('max'));
    this.numberMin.addEventListener('keydown', (event) => this.applyOnEnter(event, 'min'));
    this.numberMax.addEventListener('keydown', (event) => this.applyOnEnter(event, 'max'));
    this.applyButton.addEventListener('click', () => this.applyFilter());

    this.updateInterface();
  }

  applyOnEnter(event, changedInput) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.commitNumberInput(changedInput);
    this.applyFilter(false);
  }

  applyFilter(commitInputs = true) {
    if (commitInputs) this.commitNumberInputs();
    this.dispatchEvent(new Event('input', { bubbles: true }));
  }

  readValues() {
    const dataElement = this.querySelector('[data-width-values]');

    if (!dataElement) return [];

    try {
      return JSON.parse(dataElement.textContent)
        .filter((item) => Number.isFinite(Number(item.number)))
        .map((item) => ({ raw: String(item.raw), number: Number(item.number) }))
        .sort((a, b) => a.number - b.number);
    } catch (error) {
      return [];
    }
  }

  clamp(value) {
    return Math.min(this.maximum, Math.max(this.minimum, value));
  }

  updateFromRange(changedHandle) {
    let min = this.clamp(Number(this.rangeMin.value));
    let max = this.clamp(Number(this.rangeMax.value));

    if (min > max) {
      if (changedHandle === 'min') min = max;
      else max = min;
    }

    this.rangeMin.value = min;
    this.rangeMax.value = max;
    this.numberMin.value = min;
    this.numberMax.value = max;
    this.updateInterface();
  }

  updateNumberDraft(changedInput) {
    const input = changedInput === 'min' ? this.numberMin : this.numberMax;
    if (input.value === '') return;

    const candidate = Number(input.value);
    if (!Number.isFinite(candidate) || candidate < this.minimum || candidate > this.maximum) return;

    if (changedInput === 'min') {
      if (candidate > Number(this.rangeMax.value)) return;
      this.rangeMin.value = candidate;
    } else {
      if (candidate < Number(this.rangeMin.value)) return;
      this.rangeMax.value = candidate;
    }

    this.updateInterface();
  }

  commitNumberInput(changedInput) {
    const minCandidate = this.numberMin.value === '' ? this.minimum : Number(this.numberMin.value);
    const maxCandidate = this.numberMax.value === '' ? this.maximum : Number(this.numberMax.value);
    let min = this.clamp(Number.isFinite(minCandidate) ? minCandidate : this.minimum);
    let max = this.clamp(Number.isFinite(maxCandidate) ? maxCandidate : this.maximum);

    if (min > max) {
      if (changedInput === 'min') min = max;
      else max = min;
    }

    this.numberMin.value = min;
    this.numberMax.value = max;
    this.rangeMin.value = min;
    this.rangeMax.value = max;
    this.updateInterface();
  }

  commitNumberInputs() {
    const minCandidate = this.numberMin.value === '' ? this.minimum : Number(this.numberMin.value);
    const maxCandidate = this.numberMax.value === '' ? this.maximum : Number(this.numberMax.value);
    let min = this.clamp(Number.isFinite(minCandidate) ? minCandidate : this.minimum);
    let max = this.clamp(Number.isFinite(maxCandidate) ? maxCandidate : this.maximum);

    if (min > max) max = min;

    this.numberMin.value = min;
    this.numberMax.value = max;
    this.rangeMin.value = min;
    this.rangeMax.value = max;
    this.updateInterface();
  }

  updateInterface() {
    const min = Number(this.rangeMin.value);
    const max = Number(this.rangeMax.value);
    const span = this.maximum - this.minimum;
    const minPercent = span === 0 ? 0 : ((min - this.minimum) / span) * 100;
    const maxPercent = span === 0 ? 100 : ((max - this.minimum) / span) * 100;

    this.track.style.setProperty('--width-range-min', `${minPercent}%`);
    this.track.style.setProperty('--width-range-max', `${maxPercent}%`);
    this.rangeMin.style.zIndex = min >= this.maximum - Math.max(1, span * 0.05) ? '3' : '2';

    const selectedValues = this.values
      .filter((item) => item.number >= min && item.number <= max)
      .map((item) => item.raw);
    const isFullRange = min <= this.minimum && max >= this.maximum;

    this.parameters.replaceChildren();

    if (isFullRange) return;

    const parameterValues = selectedValues.length > 0 ? selectedValues : ['-999999999'];
    parameterValues.forEach((value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = this.parameterName;
      input.value = value;
      this.parameters.appendChild(input);
    });
  }
}

if (!customElements.get('width-range-filter')) {
  customElements.define('width-range-filter', WidthRangeFilter);
}

(function initializeWidthPreferredProducts() {
  const gridContainer = document.getElementById('ProductGridContainer');

  if (!gridContainer) return;

  let scheduled = false;

  const widthFilterIsActive = () => {
    const parameters = new URLSearchParams(window.location.search);

    return Array.from(parameters.keys()).some((parameterName) => {
      const normalizedName = parameterName.toLowerCase();
      return normalizedName.includes('filter.p.m.custom.breite_in_cm')
        || normalizedName.includes('filter.p.m.custom.breite');
    });
  };

  const sortLoadedProducts = () => {
    scheduled = false;

    const grid = gridContainer.querySelector('#product-grid');
    if (!grid) return;

    const productCards = Array.from(grid.children).filter((item) =>
      item.classList.contains('collection-product-card'),
    );

    if (productCards.length < 2) return;

    let nextOriginalPosition = productCards.reduce((highestPosition, card) => {
      const position = Number(card.dataset.widthOriginalPosition);
      return Number.isFinite(position) ? Math.max(highestPosition, position + 1) : highestPosition;
    }, 0);

    productCards.forEach((card) => {
      if (card.dataset.widthOriginalPosition === undefined) {
        card.dataset.widthOriginalPosition = String(nextOriginalPosition);
        nextOriginalPosition += 1;
      }
    });

    const widthIsActive = widthFilterIsActive();
    const sortedCards = [...productCards].sort((firstCard, secondCard) => {
      if (widthIsActive) {
        const firstPreferred = firstCard.dataset.widthPreferred === 'true' ? 1 : 0;
        const secondPreferred = secondCard.dataset.widthPreferred === 'true' ? 1 : 0;

        if (firstPreferred !== secondPreferred) return secondPreferred - firstPreferred;
      }

      return Number(firstCard.dataset.widthOriginalPosition)
        - Number(secondCard.dataset.widthOriginalPosition);
    });

    const currentChildren = Array.from(grid.children);
    let sortedProductIndex = 0;
    const orderedChildren = currentChildren.map((item) => {
      if (!item.classList.contains('collection-product-card')) return item;

      const sortedCard = sortedCards[sortedProductIndex];
      sortedProductIndex += 1;
      return sortedCard;
    });

    const orderChanged = currentChildren.some((item, index) => item !== orderedChildren[index]);
    if (orderChanged) grid.replaceChildren(...orderedChildren);
  };

  const scheduleSort = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(sortLoadedProducts);
  };

  const observer = new MutationObserver(scheduleSort);
  observer.observe(gridContainer, { childList: true, subtree: true });

  window.addEventListener('popstate', scheduleSort);
  scheduleSort();
})();
